import { ApiResponse } from "../models/api-response";
import { callApi } from "./api.service";

const apiServerUrl = import.meta.env.VITE_API_SERVER_URL;

export interface CreatePurchaseSessionRequest {
  pack_code: string;
  currency: 'USD' | 'EUR' | 'MAD';
  email?: string;
  coupon_code?: string;
  /** Originating flow — 'billing' returns to /billing after payment. */
  flow?: 'billing' | 'onboarding';
}

export interface CreatePurchaseSessionResponse {
  session_id: string;
  status: string;
  checkout_url?: string;
  tracking_id?: string;
  pack_code: string;
}

export const createPurchaseSession = async (
  accessToken: string | undefined,
  request: CreatePurchaseSessionRequest
): Promise<ApiResponse<CreatePurchaseSessionResponse>> => {
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (accessToken) {
    headers["Authorization"] = `Bearer ${accessToken}`;
  }

  const config: RequestInit = {
    method: "POST",
    headers,
    body: JSON.stringify(request),
  };
  
  return callApi<CreatePurchaseSessionResponse>(`${apiServerUrl}/api/purchase-sessions`, config);
};

export interface VerifyPurchaseSessionResponse {
  status: string;
  pack_code: string;
  user_plan_id?: string;
}

export const verifyPurchaseSession = async (
  accessToken: string | undefined, 
  sessionId: string
): Promise<ApiResponse<VerifyPurchaseSessionResponse>> => {
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };
  
  if (accessToken) {
    headers["Authorization"] = `Bearer ${accessToken}`;
  }

  const config: RequestInit = {
    method: "GET",
    headers,
  };

  return callApi<VerifyPurchaseSessionResponse>(`${apiServerUrl}/api/purchase-sessions/${sessionId}/verify`, config);
};

// --- Coupon Validation ---

export interface CouponValidateResponse {
  valid: boolean;
  discount_percent: number;
  error?: string;
}

export const validateCoupon = async (
  accessToken: string,
  code: string,
  email?: string,
): Promise<ApiResponse<CouponValidateResponse>> => {
  const config: RequestInit = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ code, email }),
  };
  return callApi<CouponValidateResponse>(`${apiServerUrl}/api/coupons/validate`, config);
};

// --- Payment Methods (Card Saving) ---

export interface SetupIntentResponse {
  client_secret: string;
  setup_intent_id: string;
  stripe_publishable_key: string;
}

export interface PaymentMethodStatusResponse {
  has_payment_method: boolean;
  methods: Array<{
    id: string;
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
  }>;
}

export const createSetupIntent = async (
  accessToken: string,
): Promise<ApiResponse<SetupIntentResponse>> => {
  const config: RequestInit = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  };
  return callApi<SetupIntentResponse>(`${apiServerUrl}/api/payment-methods/setup-intent`, config);
};

export const getPaymentMethodStatus = async (
  accessToken: string,
): Promise<ApiResponse<PaymentMethodStatusResponse>> => {
  const config: RequestInit = {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  };
  return callApi<PaymentMethodStatusResponse>(`${apiServerUrl}/api/payment-methods/status`, config);
};
